import React, { useState } from "react";
import { importPreview, importExecute, seedDatabase } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Upload, Download, FileSpreadsheet, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ImportPage() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setResult(null);
    setPreview(null);
  };

  const doPreview = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const r = await importPreview(file);
      setPreview(r);
      setMapping(r.detected_mapping || {});
    } catch (e) {
      toast.error("Errore lettura file");
    } finally {
      setLoading(false);
    }
  };

  const doImport = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const r = await importExecute(file, mapping);
      setResult(r);
      toast.success(`Importati ${r.imported} produttori`);
    } catch {
      toast.error("Errore importazione");
    } finally {
      setLoading(false);
    }
  };

  const downloadSample = () => {
    const sample = [
      "Ragione Sociale,Indirizzo,Zona,Telefono,Orario,Partita Iva,Prezzo,Gratuito,Spurgo,Frequenza,Data Scadenza",
      "Trattoria Esempio,Via Roma 1 Firenze,CENTRO,055123456,8-12,01234567890,0.30,no,0.5,MENSILE,2026-12-31",
      "Bar Test,Via Milano 5 Prato,PRATO,057411111,mattina,02234567890,,si,,SETTIMANALE,2025-06-30",
    ].join("\n");
    const blob = new Blob([sample], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "produttori_esempio.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const doSeed = async () => {
    if (!window.confirm("Ricaricare i 35 produttori di esempio? I dati esistenti NON saranno cancellati.")) return;
    setLoading(true);
    try {
      const r = await seedDatabase(false);
      if (r.skipped) {
        toast.info("Database già popolato");
      } else {
        toast.success(`Seed: ${r.produttori_seeded} produttori, ${r.mezzi_seeded} mezzi`);
      }
    } catch {
      toast.error("Errore seed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="import-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">Importazione</h1>
        <p className="text-slate-500 text-sm mt-1">CSV o Excel con produttori</p>
      </div>

      <Card className="p-5 bg-white border border-slate-200 shadow-sm">
        <div className="flex items-center gap-3 mb-3">
          <FileSpreadsheet size={24} className="text-[#1B5E20]" />
          <div>
            <div className="font-semibold">Carica file</div>
            <div className="text-xs text-slate-500">Formato supportato: .csv, .xlsx</div>
          </div>
        </div>
        <input type="file" accept=".csv,.xlsx,.xls" onChange={onFile} className="block text-sm" data-testid="import-file-input" />
        <div className="flex gap-2 mt-3">
          <Button onClick={doPreview} disabled={!file || loading} className="bg-[#1B5E20] hover:bg-[#144818] text-white min-h-[44px]" data-testid="preview-btn">
            {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : <Upload size={16} className="mr-2" />} Analizza file
          </Button>
          <Button variant="outline" onClick={downloadSample} className="min-h-[44px]" data-testid="download-sample-btn">
            <Download size={16} className="mr-2" /> Scarica esempio
          </Button>
        </div>
      </Card>

      {preview && (
        <Card className="p-5 bg-white border border-slate-200 shadow-sm" data-testid="preview-card">
          <div className="font-semibold mb-3">Mapping colonne ({preview.total_rows} righe)</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {preview.available_targets.map((target) => (
              <div key={target}>
                <label className="text-xs text-slate-500">{target}</label>
                <Select value={mapping[target] || "__none__"} onValueChange={(v) => setMapping({ ...mapping, [target]: v === "__none__" ? undefined : v })}>
                  <SelectTrigger data-testid={`map-${target}`}><SelectValue placeholder="(non mappato)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">(non mappato)</SelectItem>
                    {preview.headers.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <Button onClick={doImport} disabled={loading} className="mt-4 bg-[#F57C00] hover:bg-[#E65100] text-white min-h-[44px]" data-testid="import-btn">
            {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : null} Conferma importazione
          </Button>
        </Card>
      )}

      {result && (
        <Card className="p-5 bg-white border border-slate-200 shadow-sm" data-testid="import-result">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 size={24} className="text-[#1B5E20]" />
            <div className="font-semibold">Report importazione</div>
          </div>
          <ul className="text-sm text-slate-700 space-y-1">
            <li>Righe totali: {result.total_rows}</li>
            <li>Importati: <span className="font-bold text-[#1B5E20]">{result.imported}</span></li>
            <li>Errori: <span className="font-bold text-[#C62828]">{result.errors?.length || 0}</span></li>
          </ul>
          {result.errors?.length > 0 && (
            <div className="mt-3 max-h-48 overflow-y-auto text-xs text-slate-600 bg-slate-50 p-3 rounded">
              {result.errors.slice(0, 20).map((e, i) => (
                <div key={i} className="flex items-center gap-1"><AlertTriangle size={12} className="text-[#C62828]" /> Riga {e.row}: {e.error}</div>
              ))}
            </div>
          )}
        </Card>
      )}

      <Card className="p-5 bg-white border border-slate-200 shadow-sm">
        <div className="font-semibold mb-1">Dati di test</div>
        <div className="text-xs text-slate-500 mb-3">Carica 35 produttori fittizi e 3 mezzi per iniziare.</div>
        <Button onClick={doSeed} variant="outline" className="min-h-[44px]" data-testid="seed-btn">Carica dati di esempio</Button>
      </Card>
    </div>
  );
}
